import React, { useContext, useEffect, useState } from "react";
import { IdentifierMappingContext } from "../IdentifierMappingContext";
import { BaseModal } from "../../common/Modal/BaseModal";
import { Close } from "@material-ui/icons";
import { TextField } from "@mui/material";
import plantsIllustration from "../../../../assets/images/plants_illustration.png";
import { AddressSearchInput } from "../../common/AddressSearchInput";
import { PharmacyGroupSelect } from "./PharmacyGroupSelect";
import MixpanelService from "../../utils/MixpanelService";
import { useIdentifierMappingSubmit } from "../Hooks/useIdentifierMappingSubmit";

const defaultForm = {
  group_name: null,
  pharmacy_name: "",
  pharmacy_address: "",
};

export const TransferIdentifierModal = () => {
  const {
    allGroupNames,
    transferIdentifierModalOpen,
    coveredEntity340bId,
    setTransferIdentifierModalOpen,
    activePharmacyGroup,
  } = useContext(IdentifierMappingContext);

  const identifierAssociation = transferIdentifierModalOpen?.identifier_association;
  const pharmacy = transferIdentifierModalOpen?.pharmacy;

  const [form, setForm] = useState(defaultForm);

  const { handleSubmit, submitting } = useIdentifierMappingSubmit({
    url: "/api/mapping_requests",
    body: {
      id_340b: coveredEntity340bId,
      identifier: identifierAssociation?.identifier,
      identifier_type: identifierAssociation?.identifier_type,
      original_group_name: activePharmacyGroup,
      original_pharmacy_id: pharmacy?.id,
      original_inhouse_id: pharmacy?.public_id,
      ...form,
    },
    successMessage: "Transfer ID request submitted.",
    onSubmitSuccess: () => {
      MixpanelService.track("esp_horizon_transfer_identifier_submitted", {
        pharmacy_group: form.group_name,
        pharmacy_name: !!form.pharmacy_name,
        pharmacy_address: !!form.pharmacy_address,
      });

      setTransferIdentifierModalOpen(false);
      setForm(defaultForm);
    },
  });

  useEffect(() => {
    setForm(defaultForm);
  }, [transferIdentifierModalOpen]);

  const pharmacyGroupOptions = allGroupNames.filter(
    (groupName) => groupName !== activePharmacyGroup && groupName !== "unassigned"
  );

  return (
    <BaseModal
      open={!!transferIdentifierModalOpen}
      onClose={() => setTransferIdentifierModalOpen(false)}
      className="modal-center"
      width={470}
    >
      <div
        className="bg--light-navy py-4 px--32 flex align-items-center border--rounded-top justify-content-end"
        style={{ height: 80 }}
      >
        <img
          className="mt--32"
          style={{ width: 145, userSelect: "none", marginRight: 70 }}
          src={plantsIllustration}
          alt="plants_illustration"
        />
        <button
          className="button button--transparent border--none w--32 h--32"
          onClick={() => {
            MixpanelService.track("esp_horizon_transfer_identifier_cancelled");
            setTransferIdentifierModalOpen(undefined);
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </button>
      </div>
      <div className="p--32">
        <div className="t--500 t--xl">Transfer Identifier</div>
        <div className="mt-2 mb-4 t--md">
          Transfer an identifier out of this pharmacy group and into a different group. This action
          will impact any associated current and future transactions with the identifier.
        </div>

        <div className="mb-4">
          <div className="px--12">
            <div className="t--sm t--secondary ml-1">Identifier *</div>
            <div className="my-3">
              {`${identifierAssociation?.identifier} / ${identifierAssociation?.identifier_type?.toUpperCase()}`}
            </div>
          </div>
          <div className="mt--2rem mb-4">
            <PharmacyGroupSelect
              isTransfer={true}
              value={form.group_name}
              onChange={(e, pharmacyGroupName) => {
                MixpanelService.track("esp_horizon_transfer_pgroup");
                setForm({
                  ...form,
                  group_name: pharmacyGroupName || null,
                  pharmacy_address: "",
                  pharmacy_name: "",
                  pharmacy_public_id: null,
                  pharmacy_id: null,
                });
              }}
              options={pharmacyGroupOptions}
            />

            <div className="mt-3">
              <TextField
                fullWidth
                required
                variant="outlined"
                value={form.pharmacy_name}
                onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })}
                label="Pharmacy Name"
                placeholder="Pharmacy Name"
              />
            </div>
            <div className="mt-3">
              <AddressSearchInput
                id_340b={coveredEntity340bId}
                type={form.group_name === "in_house" ? "in_house" : "cp"}
                required
                value={
                  form.pharmacy_id
                    ? {
                        address: form.pharmacy_address,
                        pharmacy_id: form.pharmacy_id,
                        pharmacy_public_id: form.pharmacy_public_id,
                        pharmacy_name: form.pharmacy_name,
                      }
                    : null
                }
                onChange={(event, pharmacy) => {
                  setForm({
                    ...form,
                    pharmacy_address: pharmacy?.address,
                    pharmacy_id: pharmacy?.pharmacy_id,
                    pharmacy_public_id: pharmacy?.pharmacy_public_id,
                    pharmacy_name: pharmacy?.name ?? form.pharmacy_name,
                  });
                }}
                label="Pharmacy Address *"
                placeholder="Search addresses"
              />
            </div>
          </div>
        </div>

        <div className="flex align-items-center justify-content-end gap--8">
          <button
            className="button button--white"
            onClick={() => {
              MixpanelService.track("esp_horizon_transfer_identifier_cancelled");
              setTransferIdentifierModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            className="button button--blue"
            onClick={() => handleSubmit()}
            disabled={
              !form.group_name || !form.pharmacy_address || !form.pharmacy_name || submitting
            }
          >
            Submit for Approval
          </button>
        </div>
      </div>
    </BaseModal>
  );
};
